import { useModal } from '@/storybook/modal/useModal';
import { useToast } from '@/composables/useToast';
import TemplateConfigModal from '@/modules/viewer/components/templates/TemplateConfigModal.vue';
import { useViewerTemplateStore, type TemplateFormData } from '../store/useViewerTemplateStore';
import { useViewerItemStore } from '../store/useViewerItemStore';
import { closeTemplatesModal } from './useTemplatesModal';
import { useViewerTrack, TrackEvents } from './useViewerTrack';

export const useTemplateConfigModal = () => {
  const { trackItemOwner } = useViewerTrack();
  const templateStore = useViewerTemplateStore();
  const { success, error } = useToast();

  // Import item store to get current itemId
  const itemStore = useViewerItemStore();

  const initializeFormData = (slot?: string): TemplateFormData => {
    // Get template by slot from local state or defaults
    const template = slot ? templateStore.getTemplateOrDefaults(slot) : null;

    if (template) {
      return {
        templateSlot: template.slot,
        templateName: template.name,
        defaultTitle: template.itemTitle,
        defaultDescription: 'itemDescription' in template ? template.itemDescription || '' : '',
        templateDescription: 'description' in template ? template.description || '' : '',
        commentEnabled: template.commentEnabled ?? true,
        reactionEnabled: template.reactionEnabled ?? true,
        buttonEnabled: template.buttonEnabled ?? true,
        buttonText: template.buttonText || '',
        buttonLink: template.buttonLink || '',
        belongsToOrg: 'belongsToOrg' in template ? template.belongsToOrg : false,
        securitySetting: 'securitySetting' in template ? template.securitySetting : 'un',
        expireDaysEnabled: 'expireDaysEnabled' in template ? template.expireDaysEnabled : false,
        expireDays: 'expireDays' in template ? template.expireDays : 7,
        collections: 'collections' in template ? template.collections : [],
      };
    }

    // Generic defaults for new custom templates
    return {
      templateSlot: slot || '',
      templateName: 'Custom template',
      templateDescription: '',
      defaultTitle: 'Default title',
      defaultDescription: '',
      commentEnabled: true,
      reactionEnabled: true,
      buttonEnabled: true,
      buttonText: 'Click here',
      buttonLink: 'https://example.com',
      belongsToOrg: false,
      securitySetting: 'un',
      expireDaysEnabled: false,
      expireDays: 7,
      collections: [],
    };
  };

  const showTemplateConfigModal = (slot?: string) => {
    // Close the templates selection modal if it's open
    closeTemplatesModal();

    const formData = initializeFormData(slot);
    const isCustom = templateStore.isCustomTemplate(slot || '');

    const handleSubmit = async (submittedFormData: TemplateFormData) => {
      try {
        // Save template
        const savedTemplate = await templateStore.saveTemplate(submittedFormData);

        // Apply template to current item if available
        const currentItemId = itemStore.id;
        if (currentItemId && savedTemplate.id) {
          await templateStore.applyTemplate(savedTemplate.id, currentItemId);
        }

        success('Template applied');
        return { success: true, template: savedTemplate };
      } catch (err: any) {
        error('Failed to save template');
        throw err;
      }
    };

    const { open } = useModal(TemplateConfigModal, {
      hideClose: true,
      hideOverlay: true,
      layoutProps: {
        onOpened: () => {
          trackItemOwner(TrackEvents.VIEWER_TEMPLATE_EDITOR_OPENED);
        },
      },
      modalProps: {
        formData,
        isCustom,
        onSubmit: handleSubmit,
      },
    });

    return open();
  };

  return {
    showTemplateConfigModal,
  };
};
